import { useEffect, useState } from "react";
import { apiGetRequest } from "../Helpers/Requests";
import endpoints from "../Helpers/Endpoints";

// The smallest amount Keilot will take a payment for. A supplier is free to
// price a single item below it — a ₪25 cleaning job is a real job — but an
// ORDER that the member has to pay cannot land under it, so a supplier may not
// edit an order down past the line.
//
// The number lives in the community's rev-share config
// (`rs_general_data_configs.min_transaction_amount`) and is read, never
// hardcoded: one value for everyone today, per community later.

export const FALLBACK_ORDER_MINIMUM = 29;

/**
 * The minimum named in a server refusal (`code: BELOW_ORDER_MINIMUM`), or null
 * when the error is something else. The backend checks every payable too, so
 * the client shows its own message when the two disagree.
 */
export const minimumFromRefusal = (error) => {
  const data = error?.response?.data;
  if (data?.code !== "BELOW_ORDER_MINIMUM") return null;
  const minimum = Number(data.minimum);
  return Number.isFinite(minimum) ? minimum : FALLBACK_ORDER_MINIMUM;
};

/** Nothing to pay is not "below the minimum" — it is simply not a payment. */
export const isBelowOrderMinimum = (amount, minimum) =>
  amount > 0 && amount < minimum;

/**
 * What the MEMBER pays for the goods, which is what the minimum is measured
 * on. The supplier works in list prices, while the member pays after the
 * community discount, so an edited order is converted with the same discount
 * proportion the order already carries.
 *
 * `order_summary.total` is NOT it — that one is the supplier's own share.
 */
export const memberPaidValue = (grossAfterEdit, orderSummary) => {
  const gross = Number(orderSummary?.price) || 0;
  const discount = Number(orderSummary?.community_discount) || 0;
  if (gross <= 0) return grossAfterEdit;
  const keptRatio = Math.max(0, (gross - discount) / gross);
  return Math.round(grossAfterEdit * keptRatio * 100) / 100;
};

let cachedMinimum = null;

/** The amount below which this community will not take a payment. */
export const useOrderMinimum = () => {
  const [minimum, setMinimum] = useState(
    cachedMinimum ?? FALLBACK_ORDER_MINIMUM,
  );

  useEffect(() => {
    if (cachedMinimum != null) return;

    let active = true;
    apiGetRequest(endpoints.getOrderMinimum)
      .then((response) => {
        const value = Number(response?.data?.minimum);
        if (Number.isFinite(value) && value > 0) {
          cachedMinimum = value;
          if (active) setMinimum(value);
        }
      })
      .catch(() => {
        // Keep the house rule rather than letting an edit through unguarded.
      });

    return () => {
      active = false;
    };
  }, []);

  return minimum;
};
